import saveAs from 'file-saver';
import React, { useState, useMemo } from 'react';
import { BrandImage } from 'src/components/AtlasV2Components/AtlasV2SalesPageComponents/AtlasV2MarketShareGrid';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import EntityTable from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTable';
import { EntityLink } from 'src/components/BeaconV2Components/MetricList/headColumnDefinitions';
import { safeDivide } from 'src/utils/app';
import { getYearForWeekId, getPreviousYearWeekId } from 'src/utils/dateUtils';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import {
  useAppSelector,
  useAvailableTImePeriod,
  useCurrentAndLastYearAvailableTimePeriod,
  useLastYearAvailableTimePeriod,
  useMetricFormatter
} from 'src/utils/Hooks';
import { useDorisData } from 'src/utils/Hooks/useDorisData';

const mainTimePeriodToPayload = {
  cd: 'customdate',
  '1w': 'lastweek',
  '4w': 'last4weeks',
  '13w': 'last13weeks',
  '26w': 'last26weeks',
  '52w': 'last52weeks',
  ly: 'lastyear',
  ytd: 'ytd'
};

const timePeriodNameConversion = {
  ['Last Week']: 'lastweek',
  ['Last 4 Weeks']: 'last4weeks',
  ['Last 13 Weeks']: 'last13weeks',
  ['Last 26 Weeks']: 'last26weeks',
  ['Last 52 Weeks']: 'last52weeks',
  ['Last Year']: 'lastyear',
  ['YTD']: 'ytd',
  ['Custom Date']: 'customdate'
};

/**
 * Converts time period array data into an object with period names as keys
 * @param timePeriodData - Array of [startWeek, endWeek, periodName] tuples
 * @returns Object with period names as keys and {startWeek, endWeek} as values
 */
const createTimeRangeObject = (
  timePeriodData: (string | number)[][]
): Record<string, { startWeek: number; endWeek: number }> => {
  return timePeriodData.reduce((acc, [startWeek, endWeek, periodName]) => {
    if (timePeriodNameConversion[periodName as keyof typeof timePeriodNameConversion]) {
      acc[timePeriodNameConversion[periodName]] = { startWeek: +startWeek, endWeek: +endWeek };
    }
    return acc;
  }, {} as Record<string, { startWeek: number; endWeek: number }>);
};

const generateBrandValuesTimePeriodMapWithMetadata = (data) => {
  const timePeriods = Object.values(timePeriodNameConversion);

  const brandMap = new Map();

  // Initialize brand entries with metadata
  for (const brandId of data?.data?.brandIds || []) {
    const meta = data?.data?.brandMetadata?.[brandId];
    brandMap.set(brandId, {
      brandId,
      brandName: meta?.brandName || null
    });
  }

  // Populate values by time period
  for (const period of timePeriods) {
    const records = data?.data?.[period]?.records || [];
    for (const { fieldKey: brandId, value } of records) {
      if (!brandMap.has(brandId)) {
        brandMap.set(brandId, { brandId });
      }
      brandMap.get(brandId)[period] = value;
    }
  }

  return brandMap;
};

const totalRow = {
  cd: 1,
  '1w': 1,
  '4w': 1,
  '13w': 1,
  '26w': 1,
  '52w': 1,
  ly: 1,
  ytd: 1,
  name: 'totalRow',
  rank: 'totalRow'
};

const generateCleanNumber = (value) => (Math.abs(value) < Number.EPSILON ? 0 : value);

const useColumnDefinition = (
  mainTimePeriodId: string,
  onImageLoadingChange?: (brandId: string, isLoading: boolean) => void
) => {
  const formatMetric = useMetricFormatter();

  const COL_DEFS = [
    {
      headerName: 'Rank',
      field: 'rank',
      valueFormatter: (value) => {
        if (value === 'otherBrandIds' || value === 'totalRow') {
          return <div style={{ fontSize: '14px' }}>-</div>;
        }

        return <div style={{ fontSize: '14px' }}>{value}</div>;
      }
    },
    // Brand Entity
    {
      headerName: 'Brands',
      field: 'name',
      valueFormatter: (value, row) => {
        if (value === 'otherBrandIds') {
          return <div style={{ fontSize: '14px' }}>Other</div>;
        }
        if (value === 'totalRow') {
          return <div style={{ fontSize: '14px' }}>Total</div>;
        }

        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {React.createElement(BrandImage as any, {
              row,
              imageMode: 'base64',
              onLoadingChange: onImageLoadingChange
            })}
            <EntityLink id={row.brandId} entityType="brand">
              {row.name}
            </EntityLink>
          </div>
        );
      }
    },
    ...(mainTimePeriodId === 'cd'
      ? [
          {
            field: 'cd',
            headerName: 'Custom\n Date',
            valueFormatter: (value) => {
              const cleanNumber = generateCleanNumber(value);
              return (
                <div style={{ fontSize: '14px' }}>
                  {formatMetric(cleanNumber, METRICTYPE.PERCENT, { decimalPlaces: 2 })}
                </div>
              );
            }
          }
        ]
      : []),
    {
      field: '1w',
      headerName: 'LAST\n WEEK',
      valueFormatter: (value) => {
        const cleanNumber = generateCleanNumber(value);
        return (
          <div style={{ fontSize: '14px' }}>{formatMetric(cleanNumber, METRICTYPE.PERCENT, { decimalPlaces: 2 })}</div>
        );
      }
    },

    {
      field: '4w',
      headerName: 'LAST\n 4 WEEKS',
      valueFormatter: (value) => {
        const cleanNumber = generateCleanNumber(value);
        return (
          <div style={{ fontSize: '14px' }}>{formatMetric(cleanNumber, METRICTYPE.PERCENT, { decimalPlaces: 2 })}</div>
        );
      }
    },

    {
      field: '13w',
      headerName: 'LAST\n 13 WEEK',
      valueFormatter: (value) => {
        const cleanNumber = generateCleanNumber(value);
        return (
          <div style={{ fontSize: '14px' }}>{formatMetric(cleanNumber, METRICTYPE.PERCENT, { decimalPlaces: 2 })}</div>
        );
      }
    },

    {
      field: '26w',
      headerName: 'LAST\n 26 WEEK',
      valueFormatter: (value) => {
        const cleanNumber = generateCleanNumber(value);
        return (
          <div style={{ fontSize: '14px' }}>{formatMetric(cleanNumber, METRICTYPE.PERCENT, { decimalPlaces: 2 })}</div>
        );
      }
    },

    {
      field: '52w',
      headerName: 'LAST\n 52 WEEK',
      valueFormatter: (value) => {
        const cleanNumber = generateCleanNumber(value);
        return (
          <div style={{ fontSize: '14px' }}>{formatMetric(cleanNumber, METRICTYPE.PERCENT, { decimalPlaces: 2 })}</div>
        );
      }
    },
    {
      field: 'ly',
      headerName: 'LAST\n YEAR',
      valueFormatter: (value) => {
        const cleanNumber = generateCleanNumber(value);
        return (
          <div style={{ fontSize: '14px' }}>{formatMetric(cleanNumber, METRICTYPE.PERCENT, { decimalPlaces: 2 })}</div>
        );
      }
    },
    {
      field: 'ytd',
      headerName: 'YTD',
      valueFormatter: (value) => {
        const cleanNumber = generateCleanNumber(value);
        return (
          <div style={{ fontSize: '14px' }}>{formatMetric(cleanNumber, METRICTYPE.PERCENT, { decimalPlaces: 2 })}</div>
        );
      }
    }
  ];

  return COL_DEFS;
};

const getMetricType = {
  retailSales: 'RetailSalesGrid',
  unitsSold: 'UnitsSoldGrid'
};

const getDisplayTitle = (metricType: 'retailSales' | 'unitsSold') => {
  if (metricType === 'retailSales') {
    return 'Market Share Grid of Retail Sales';
  }
  return 'Market Share Grid of Units Sold';
};

/**
 *
 * Market Share grid that will show up in category, brand, subcategory entity in sales page
 */
const AtlasMarketShareGridV2 = ({ metricType }: { metricType: 'retailSales' | 'unitsSold' }) => {
  const {
    id: mainTimePeriodId,
    startWeek: mainStartWeek,
    endWeek: mainEndWeek
  } = useAppSelector((state) => state.mainTimePeriod);
  const [imageLoadingStates, setImageLoadingStates] = useState<Map<string, boolean>>(new Map());

  const { startAndEndWeeks } = useAvailableTImePeriod();
  const lastYearStartAndWeeks = useLastYearAvailableTimePeriod();

  const timeRangeObject = createTimeRangeObject(startAndEndWeeks);

  // Current year API time range
  const currentYearApiTimeRange = {
    lastweek: { startWeekId: timeRangeObject.lastweek.startWeek, endWeekId: timeRangeObject.lastweek.endWeek },
    last4weeks: { startWeekId: timeRangeObject.last4weeks.startWeek, endWeekId: timeRangeObject.last4weeks.endWeek },
    last13weeks: {
      startWeekId: timeRangeObject.last13weeks.startWeek,
      endWeekId: timeRangeObject.last13weeks.endWeek
    },
    last26weeks: {
      startWeekId: timeRangeObject.last26weeks.startWeek,
      endWeekId: timeRangeObject.last26weeks.endWeek
    },
    last52weeks: {
      startWeekId: timeRangeObject.last52weeks.startWeek,
      endWeekId: timeRangeObject.last52weeks.endWeek
    },
    lastYear: {
      startWeekId: timeRangeObject.lastyear.startWeek,
      endWeekId: timeRangeObject.lastyear.endWeek
    },
    ytd: { startWeekId: timeRangeObject.ytd.startWeek, endWeekId: timeRangeObject.ytd.endWeek },
    sortBy: mainTimePeriodToPayload[mainTimePeriodId]
  };

  // Market Share Values
  const { data: marketTotalData, isLoading: isMarketTotalDataLoading } = useDorisData({
    methodName: 'sales/GetSalesMetrics',
    metricType: getMetricType[metricType],
    fieldName: 'retailer',
    timeRange: currentYearApiTimeRange,
    resultCount: 1,
    skipComparison: true
  });

  // Sales values by Brand
  const { data: salesByBrandData, isLoading: isSalesByBrandDataLoading } = useDorisData({
    methodName: 'sales/GetSalesMetrics',
    metricType: getMetricType[metricType],
    fieldName: 'brand',
    timeRange: currentYearApiTimeRange,
    resultCount: 21,
    skipComparison: true
  });

  const lastYearApiTimeRange = useMemo(() => {
    const correctedTimeRangeObject = createTimeRangeObject(lastYearStartAndWeeks);

    // The backend computes the `sortBy` period's bucket from the payload's top-level
    // startWeekId/endWeekId instead of the range passed in `timeRange`, so sortBy must
    // match the main period whose window is sent as the top-level week range (GPT-657).
    const timeRange: any = {
      sortBy: mainTimePeriodToPayload[mainTimePeriodId] ?? 'last52weeks'
    };
    if (correctedTimeRangeObject.lastweek?.startWeek && correctedTimeRangeObject.lastweek?.endWeek) {
      timeRange.lastweek = {
        startWeekId: correctedTimeRangeObject.lastweek.startWeek,
        endWeekId: correctedTimeRangeObject.lastweek.endWeek
      };
    }

    if (correctedTimeRangeObject.last4weeks?.startWeek && correctedTimeRangeObject.last4weeks?.endWeek) {
      timeRange.last4weeks = {
        startWeekId: correctedTimeRangeObject.last4weeks.startWeek,
        endWeekId: correctedTimeRangeObject.last4weeks.endWeek
      };
    }

    if (correctedTimeRangeObject.last13weeks?.startWeek && correctedTimeRangeObject.last13weeks?.endWeek) {
      timeRange.last13weeks = {
        startWeekId: correctedTimeRangeObject.last13weeks.startWeek,
        endWeekId: correctedTimeRangeObject.last13weeks.endWeek
      };
    }

    if (correctedTimeRangeObject.last26weeks?.startWeek && correctedTimeRangeObject.last26weeks?.endWeek) {
      timeRange.last26weeks = {
        startWeekId: correctedTimeRangeObject.last26weeks.startWeek,
        endWeekId: correctedTimeRangeObject.last26weeks.endWeek
      };
    }

    if (correctedTimeRangeObject.last52weeks?.startWeek && correctedTimeRangeObject.last52weeks?.endWeek) {
      timeRange.last52weeks = {
        startWeekId: correctedTimeRangeObject.last52weeks.startWeek,
        endWeekId: correctedTimeRangeObject.last52weeks.endWeek
      };
    }

    if (correctedTimeRangeObject.lastyear?.startWeek && correctedTimeRangeObject.lastyear?.endWeek) {
      timeRange.lastYear = {
        startWeekId: correctedTimeRangeObject.lastyear.startWeek,
        endWeekId: correctedTimeRangeObject.lastyear.endWeek
      };
    }

    if (correctedTimeRangeObject.ytd?.startWeek && correctedTimeRangeObject.ytd?.endWeek) {
      timeRange.ytd = {
        startWeekId: correctedTimeRangeObject.ytd.startWeek,
        endWeekId: correctedTimeRangeObject.ytd.endWeek
      };
    }

    return timeRange;
  }, [lastYearStartAndWeeks, mainTimePeriodId]);

  // Top-level week window for the last-year requests. Without this the payload's
  // startWeekId/endWeekId default to the CURRENT main period, and the backend fills the
  // sortBy period's bucket with current-year data (GPT-657: L52W LY duplicated L52W).
  const lastYearCustomTimePeriods = {
    startWeekId: getPreviousYearWeekId(+mainStartWeek),
    endWeekId: getPreviousYearWeekId(+mainEndWeek)
  };

  // Last year market total; top-level week window must be the last-year window (GPT-657)
  const { data: lastYearMarketTotalData, isLoading: isLastYearMarketTotalDataLoading } = useDorisData({
    methodName: 'sales/GetSalesMetrics',
    metricType: getMetricType[metricType],
    fieldName: 'retailer',
    timeRange: lastYearApiTimeRange,
    customTimePeriods: lastYearCustomTimePeriods,
    resultCount: 1,
    skipComparison: true,
    enabled: true,
    queryKey: `lastYear-marketTotal-${metricType}`,
    queryOptions: {
      staleTime: 0,
      refetchOnWindowFocus: false
    }
  });

  // Last year sales by brand; top-level week window must be the last-year window (GPT-657)
  const { data: lastYearSalesByBrandData, isLoading: isLastYearSalesByBrandDataLoading } = useDorisData({
    methodName: 'sales/GetSalesMetrics',
    metricType: getMetricType[metricType],
    fieldName: 'brand',
    timeRange: lastYearApiTimeRange,
    customTimePeriods: lastYearCustomTimePeriods,
    resultCount: 21,
    skipComparison: true,
    enabled: true,
    queryKey: `lastYear-brandSales-${metricType}`,
    queryOptions: {
      staleTime: 0,
      refetchOnWindowFocus: false
    }
  });

  const currentAndLastYearTimePeriod = useCurrentAndLastYearAvailableTimePeriod();

  const formatMetric = useMetricFormatter();

  const handleImageLoadingChange = (brandId: string, isLoading: boolean) => {
    setImageLoadingStates((prev) => {
      const newMap = new Map(prev);
      if (isLoading) {
        newMap.set(brandId, true);
      } else {
        newMap.delete(brandId);
      }
      return newMap;
    });
  };

  const fields = useColumnDefinition(mainTimePeriodId, handleImageLoadingChange);

  // Generate brand data map with CURRENT year data only (for display table)
  const brandSalesMap = generateBrandValuesTimePeriodMapWithMetadata(salesByBrandData);

  // This is showing first 10 rows of the table
  const rows = Array.from(brandSalesMap)
    .map(([id, record], index) => {
      return {
        rank: index + 1,
        brandId: id,
        name: record.brandName,
        cd: safeDivide(
          record[timePeriodNameConversion['Custom Date']],
          marketTotalData?.data?.[timePeriodNameConversion['Custom Date']]?.records?.[0]?.value
        ),
        '1w': safeDivide(
          record[timePeriodNameConversion['Last Week']],
          marketTotalData?.data?.[timePeriodNameConversion['Last Week']]?.records?.[0]?.value
        ),
        '4w': safeDivide(
          record[timePeriodNameConversion['Last 4 Weeks']],
          marketTotalData?.data?.[timePeriodNameConversion['Last 4 Weeks']]?.records?.[0]?.value
        ),
        '13w': safeDivide(
          record[timePeriodNameConversion['Last 13 Weeks']],
          marketTotalData?.data?.[timePeriodNameConversion['Last 13 Weeks']]?.records?.[0]?.value
        ),
        '26w': safeDivide(
          record[timePeriodNameConversion['Last 26 Weeks']],
          marketTotalData?.data?.[timePeriodNameConversion['Last 26 Weeks']]?.records?.[0]?.value
        ),
        '52w': safeDivide(
          record[timePeriodNameConversion['Last 52 Weeks']],
          marketTotalData?.data?.[timePeriodNameConversion['Last 52 Weeks']]?.records?.[0]?.value
        ),
        ly: safeDivide(
          record[timePeriodNameConversion['Last Year']],
          marketTotalData?.data?.[timePeriodNameConversion['Last Year']]?.records?.[0]?.value
        ),
        ytd: safeDivide(
          record[timePeriodNameConversion['YTD']],
          marketTotalData?.data?.[timePeriodNameConversion['YTD']]?.records?.[0]?.value
        )
      };
    })
    .filter((row) => row.rank < 11);

  const salesSumRow = rows.reduce(
    (acc, curr) => {
      acc['1w'] -= curr['1w'];
      acc['4w'] -= curr['4w'];
      acc['13w'] -= curr['13w'];
      acc['26w'] -= curr['26w'];
      acc['52w'] -= curr['52w'];
      acc.cd -= curr.cd;
      acc.ly -= curr.ly;
      acc.ytd -= curr.ytd;
      return acc;
    },
    {
      '1w': 1,
      '4w': 1,
      '13w': 1,
      '26w': 1,
      '52w': 1,
      ly: 1,
      ytd: 1,
      cd: 1,
      name: 'otherBrandIds',
      rank: 'otherBrandIds'
    }
  );

  const convertSeriesToDelimitedData = () => {
    try {
      const header = ['Date Range ->'];
      // fill the header row

      currentAndLastYearTimePeriod.forEach((tpSet) => {
        const { startWeek, endWeek } = tpSet[0];
        const { prevYearStartWeek, prevYearEndWeek } = tpSet[1];

        header.push(
          `${getYearForWeekId(+endWeek)} (${startWeek} - ${endWeek})`, // sales
          '', // share
          `${getYearForWeekId(+prevYearEndWeek)} (${prevYearStartWeek} - ${prevYearEndWeek})`, // last year sales
          '', // last year share
          '', // YoY sales
          '' // YoY share
        );
      });

      const subHeader = ['Brand'];
      currentAndLastYearTimePeriod.forEach((tpSet) => {
        const { shortDisplayName } = tpSet[0];
        subHeader.push(
          `${shortDisplayName} Sales`,
          `${shortDisplayName} Share`,
          `${shortDisplayName} LY Sales`,
          `${shortDisplayName} LY Share`,
          `${shortDisplayName} YoY Sales (%)`,
          `${shortDisplayName} YoY Share (%)`
        );
      });

      const calculateMetrics = (brand, periodKey) => {
        const currentSales = brand[periodKey];
        const lastYearShareMap = lastYearSalesByBrandData?.data?.[periodKey]?.records?.reduce((acc, curr) => {
          acc[curr.fieldKey] = curr.value;
          return acc;
        }, {});
        const lastYearSales = lastYearShareMap?.[brand.brandId];
        const currentMarketTotal = marketTotalData?.data?.[periodKey]?.records?.[0]?.value;
        const lastYearMarketTotal = lastYearMarketTotalData?.data?.[periodKey]?.records?.[0]?.value;
        const currentShare = safeDivide(currentSales, currentMarketTotal);
        const lastYearShare = safeDivide(lastYearSales, lastYearMarketTotal);
        const yoySales = safeDivide(currentSales, lastYearSales) - 1;
        const yoyShare = safeDivide(currentShare, lastYearShare) - 1;

        return [
          currentSales || 0,
          formatMetric(currentShare, METRICTYPE.PERCENT, { decimalPlaces: 2 }),
          lastYearSales || 0,
          formatMetric(lastYearShare, METRICTYPE.PERCENT, { decimalPlaces: 2 }),
          isNaN(yoySales) ? '0.0000' : yoySales.toFixed(4),
          isNaN(yoyShare) ? '0.0000' : yoyShare.toFixed(4)
        ];
      };

      // Helper function to properly escape CSV fields
      const escapeCSVField = (field: any): string => {
        if (field === null || field === undefined) return '';
        const fieldStr = String(field);

        // If field contains comma, double quote, or newline, wrap in quotes
        if (fieldStr.includes(',') || fieldStr.includes('"') || fieldStr.includes('\n')) {
          // Escape existing quotes by doubling them
          return `"${fieldStr.replace(/"/g, '""')}"`;
        }
        return fieldStr;
      };

      const brandRows = Array.from(brandSalesMap.values()).map((brand) => {
        const brandRow = [brand.brandName];
        const periods = ['lastweek', 'last4weeks', 'last13weeks', 'last26weeks', 'last52weeks', 'lastyear', 'ytd'];

        periods.forEach((periodKey) => {
          brandRow.push(...calculateMetrics(brand, periodKey));
        });

        return brandRow;
      });

      // Map each row and escape fields that contain commas
      const csvArray = [header, [], subHeader, ...brandRows].map((row) => row.map(escapeCSVField).join(',')).join('\n');
      const blob = new Blob([csvArray], { type: 'text/csv;charset=utf-8' });
      const fileName = `market_share_${metricType}_data`;
      saveAs(blob, `${fileName}.csv`);
    } catch (error) {
      console.error('Fail to generate - market share grid ', error);
    }

    return Promise.resolve();
  };

  return (
    <>
      <BeaconChartWithLegend
        showTitleSummary
        title={getDisplayTitle(metricType)}
        convertSeriesToDelimitedData={() => convertSeriesToDelimitedData()}
      >
        <EntityTable
          columnDefs={fields}
          shouldModifyColumnDefs={false}
          showPagination={false}
          isLoading={
            isMarketTotalDataLoading ||
            isSalesByBrandDataLoading ||
            isLastYearMarketTotalDataLoading ||
            isLastYearSalesByBrandDataLoading ||
            imageLoadingStates.size > 0
          }
          rowData={[...rows, salesSumRow, totalRow]}
          skeletonRows={13}
        />
      </BeaconChartWithLegend>
    </>
  );
};

export default AtlasMarketShareGridV2;
